import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaCreditCard, FaQrcode, FaBolt, FaCheck, FaTimes,
    FaEye, FaUpload, FaToggleOn, FaToggleOff, FaFileInvoiceDollar,
    FaLightbulb, FaHourglassHalf, FaCheckCircle, FaTimesCircle, FaSpinner,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import './Dashboard.css';
import './AdminPayments.css';

const API = 'https://aayushrealproperties.in/nimaibackend/public/api';

const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const StatusBadge = ({ status }) => {
    const map = {
        pending:  { label: 'Pending',  cls: 'pending' },
        approved: { label: 'Approved', cls: 'approved' },
        rejected: { label: 'Rejected', cls: 'rejected' },
    };
    const s = map[status] || map.pending;
    return <span className={`pay-status-badge ${s.cls}`}>{s.label}</span>;
};

const FeatStatusBadge = ({ status }) => {
    const map = {
        pending:     { label: 'Pending',     cls: 'pending' },
        in_progress: { label: 'In Progress', cls: 'inprogress' },
        completed:   { label: 'Completed',   cls: 'approved' },
        rejected:    { label: 'Rejected',    cls: 'rejected' },
    };
    const s = map[status] || map.pending;
    return <span className={`pay-status-badge ${s.cls}`}>{s.label}</span>;
};

const AdminPayments = () => {
    const [tab, setTab] = useState('requests');

    // Settings state
    const [, setSettings] = useState(null);
    const [method, setMethod] = useState('manual');
    const [qrPreview, setQrPreview] = useState(null);
    const [qrFile, setQrFile] = useState(null);
    const [easebuzzKey, setEasebuzzKey] = useState('');
    const [easebuzzSalt, setEasebuzzSalt] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);
    const qrInputRef = useRef();

    // Payments state
    const [payments, setPayments] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(true);
    const [viewScreenshot, setViewScreenshot] = useState(null);
    const [actionLoading, setActionLoading] = useState('');

    // Feature requests state
    const [features, setFeatures] = useState([]);
    const [loadingFeatures, setLoadingFeatures] = useState(false);
    const [featureAction, setFeatureAction] = useState('');
    const [featureFilter, setFeatureFilter] = useState('all');

    const token = localStorage.getItem('admin_token');
    const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' };

    useEffect(() => {
        fetchSettings();
        fetchPayments();
        fetchFeatures();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const parseJson = async (res) => {
        const text = await res.text();
        const i = text.search(/\{|\[/);
        return JSON.parse(i !== -1 ? text.substring(i) : text);
    };

    const fetchSettings = async () => {
        try {
            const res = await fetch(`${API}/admin/payment-settings`, { headers });
            const json = await parseJson(res);
            if (json.status) {
                setSettings(json.data);
                setMethod(json.data.method || 'manual');
                setEasebuzzKey(json.data.easebuzz_key || '');
                setEasebuzzSalt(json.data.easebuzz_salt || '');
                if (json.data.qr_code_image) {
                    setQrPreview(`https://aayushrealproperties.in/nimaibackend/public/storage/${json.data.qr_code_image}`);
                }
            }
        } catch { toast.error('Failed to load settings'); }
    };

    const fetchPayments = async () => {
        setLoadingPayments(true);
        try {
            const res = await fetch(`${API}/admin/plan-payments`, { headers });
            const json = await parseJson(res);
            if (json.status) setPayments(json.data || []);
        } catch { toast.error('Failed to load payments'); }
        finally { setLoadingPayments(false); }
    };

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            const form = new FormData();
            form.append('method', method);
            if (qrFile) form.append('qr_code', qrFile);
            if (method === 'easebuzz') {
                form.append('easebuzz_key', easebuzzKey);
                form.append('easebuzz_salt', easebuzzSalt);
            }
            const res = await fetch(`${API}/admin/payment-settings`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
                body: form,
            });
            const json = await parseJson(res);
            if (json.status) { toast.success('Settings saved'); fetchSettings(); }
            else toast.error('Failed to save');
        } catch { toast.error('Network error'); }
        finally { setSavingSettings(false); }
    };

    const handleApprove = async (id) => {
        setActionLoading(`approve-${id}`);
        try {
            const res = await fetch(`${API}/admin/plan-payments/${id}/approve`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
            });
            const json = await parseJson(res);
            if (json.status) {
                toast.success(`Approved — Invoice ${json.invoice_number} generated`);
                setPayments(prev => prev.map(p => p.id === id ? { ...p, status: 'approved' } : p));
            } else {
                toast.error(json.message || 'Failed');
            }
        } catch { toast.error('Network error'); }
        finally { setActionLoading(''); }
    };

    const handleReject = async (id) => {
        if (!window.confirm('Reject this payment?')) return;
        setActionLoading(`reject-${id}`);
        try {
            const res = await fetch(`${API}/admin/plan-payments/${id}/reject`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
            });
            const json = await parseJson(res);
            if (json.status) {
                toast.success('Rejected');
                setPayments(prev => prev.map(p => p.id === id ? { ...p, status: 'rejected' } : p));
            }
        } catch { toast.error('Network error'); }
        finally { setActionLoading(''); }
    };

    const fetchFeatures = async () => {
        setLoadingFeatures(true);
        try {
            const res = await fetch(`${API}/admin/feature-requests`, { headers });
            const json = await parseJson(res);
            if (json.status) setFeatures(json.data || []);
        } catch { toast.error('Failed to load feature requests'); }
        finally { setLoadingFeatures(false); }
    };

    const handleFeatureStatus = async (id, status) => {
        setFeatureAction(`${status}-${id}`);
        try {
            const res = await fetch(`${API}/admin/feature-requests/${id}`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status }),
            });
            const json = await parseJson(res);
            if (json.status) {
                toast.success(`Marked as ${status}`);
                setFeatures(prev => prev.map(f => f.id === id ? { ...f, status } : f));
            }
        } catch { toast.error('Network error'); }
        finally { setFeatureAction(''); }
    };

    const pending = payments.filter(p => p.status === 'pending').length;
    const pendingFeatures = features.filter(f => f.status === 'pending').length;

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Payments <span>&amp; Settings</span></h1>
                        <p>Manage payment method, QR code, and review client payment requests.</p>
                    </div>
                </header>

                {/* Tabs */}
                <div className="pay-tabs">
                    <button
                        className={`pay-tab ${tab === 'requests' ? 'active' : ''}`}
                        onClick={() => setTab('requests')}
                    >
                        <FaCreditCard /> Payment Requests
                        {pending > 0 && <span className="pay-tab-badge">{pending}</span>}
                    </button>
                    <button
                        className={`pay-tab ${tab === 'settings' ? 'active' : ''}`}
                        onClick={() => setTab('settings')}
                    >
                        <FaQrcode /> Payment Settings
                    </button>
                    <button
                        className={`pay-tab ${tab === 'features' ? 'active' : ''}`}
                        onClick={() => setTab('features')}
                    >
                        <FaLightbulb /> Feature Requests
                        {pendingFeatures > 0 && <span className="pay-tab-badge">{pendingFeatures}</span>}
                    </button>
                </div>

                {/* ─── SETTINGS TAB ─── */}
                {tab === 'settings' && (
                    <div className="pay-settings-card">
                        <div className="pay-method-toggle">
                            <div
                                className={`pay-method-option ${method === 'manual' ? 'active' : ''}`}
                                onClick={() => setMethod('manual')}
                            >
                                <FaQrcode />
                                <div>
                                    <div className="pay-method-name">Manual (QR Code)</div>
                                    <div className="pay-method-sub">Client scans QR and uploads screenshot</div>
                                </div>
                                {method === 'manual' ? <FaToggleOn className="pay-toggle-icon on" /> : <FaToggleOff className="pay-toggle-icon" />}
                            </div>
                            <div
                                className={`pay-method-option ${method === 'easebuzz' ? 'active' : ''}`}
                                onClick={() => setMethod('easebuzz')}
                            >
                                <FaBolt />
                                <div>
                                    <div className="pay-method-name">Easebuzz Gateway</div>
                                    <div className="pay-method-sub">Automated online payment</div>
                                </div>
                                {method === 'easebuzz' ? <FaToggleOn className="pay-toggle-icon on" /> : <FaToggleOff className="pay-toggle-icon" />}
                            </div>
                        </div>

                        {method === 'manual' && (
                            <div className="pay-qr-section">
                                <label className="pay-field-label">Payment QR Code</label>
                                <div className="pay-qr-wrap">
                                    {qrPreview ? (
                                        <img src={qrPreview} alt="QR Code" className="pay-qr-img" />
                                    ) : (
                                        <div className="pay-qr-placeholder">
                                            <FaQrcode />
                                            <span>No QR uploaded</span>
                                        </div>
                                    )}
                                    <button className="pay-qr-upload-btn" onClick={() => qrInputRef.current.click()}>
                                        <FaUpload /> {qrPreview ? 'Change QR' : 'Upload QR'}
                                    </button>
                                    <input
                                        ref={qrInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={e => {
                                            const f = e.target.files[0];
                                            if (f) { setQrFile(f); setQrPreview(URL.createObjectURL(f)); }
                                        }}
                                    />
                                </div>
                                <p className="pay-qr-hint">Upload your UPI QR code image. Clients will scan this to make payment.</p>
                            </div>
                        )}

                        {method === 'easebuzz' && (
                            <div className="pay-eb-section">
                                <label className="pay-field-label">Easebuzz Merchant Key</label>
                                <input
                                    className="pay-input"
                                    placeholder="Enter merchant key"
                                    value={easebuzzKey}
                                    onChange={e => setEasebuzzKey(e.target.value)}
                                />
                                <label className="pay-field-label" style={{ marginTop: 14 }}>Easebuzz Salt</label>
                                <input
                                    className="pay-input"
                                    placeholder="Enter salt"
                                    value={easebuzzSalt}
                                    onChange={e => setEasebuzzSalt(e.target.value)}
                                />
                                <p className="pay-qr-hint" style={{ marginTop: 10 }}>Easebuzz integration will be activated once keys are saved.</p>
                            </div>
                        )}

                        <button
                            className="pay-save-btn"
                            onClick={handleSaveSettings}
                            disabled={savingSettings}
                        >
                            {savingSettings ? 'Saving…' : <><FaCheck /> Save Settings</>}
                        </button>
                    </div>
                )}

                {/* ─── REQUESTS TAB ─── */}
                {tab === 'requests' && (
                    <div className="pay-table-wrap">
                        {loadingPayments ? (
                            <div className="clients-loading"><p>Loading payments…</p></div>
                        ) : payments.length === 0 ? (
                            <div className="clients-empty">
                                <FaFileInvoiceDollar />
                                <h3>No payment requests yet</h3>
                                <p>Client plan payment submissions will appear here.</p>
                            </div>
                        ) : (
                            <table className="clients-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Plan</th>
                                        <th>Duration</th>
                                        <th>Amount</th>
                                        <th>GST (18%)</th>
                                        <th>Total</th>
                                        <th>Submitted</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map(p => (
                                        <tr key={p.id}>
                                            <td>
                                                <div className="client-full-name">{p.first_name} {p.last_name}</div>
                                                <div className="client-email-sub">{p.email}</div>
                                            </td>
                                            <td><span className="type-badge">{p.plan}</span></td>
                                            <td>{p.duration} month{p.duration > 1 ? 's' : ''}</td>
                                            <td>{formatCurrency(p.base_amount)}</td>
                                            <td>{formatCurrency(p.gst_amount)}</td>
                                            <td><strong>{formatCurrency(p.total_amount)}</strong></td>
                                            <td>{formatDate(p.created_at)}</td>
                                            <td><StatusBadge status={p.status} /></td>
                                            <td>
                                                {p.screenshot && (
                                                    <button
                                                        className="tbl-btn view"
                                                        title="View Screenshot"
                                                        onClick={() => setViewScreenshot(
                                                            `https://aayushrealproperties.in/nimaibackend/public/storage/${p.screenshot}`
                                                        )}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                )}
                                                {p.status === 'pending' && (
                                                    <>
                                                        <button
                                                            className="tbl-btn approve"
                                                            title="Approve"
                                                            disabled={!!actionLoading}
                                                            onClick={() => handleApprove(p.id)}
                                                        >
                                                            {actionLoading === `approve-${p.id}` ? '…' : <FaCheck />}
                                                        </button>
                                                        <button
                                                            className="tbl-btn del"
                                                            title="Reject"
                                                            disabled={!!actionLoading}
                                                            onClick={() => handleReject(p.id)}
                                                        >
                                                            {actionLoading === `reject-${p.id}` ? '…' : <FaTimes />}
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* ─── FEATURE REQUESTS TAB ─── */}
                {tab === 'features' && (
                    <div className="pay-table-wrap">
                        {/* Filter bar */}
                        <div className="feat-filter-bar">
                            {['all', 'pending', 'in_progress', 'completed', 'rejected'].map(f => (
                                <button
                                    key={f}
                                    className={`feat-filter-btn ${featureFilter === f ? 'active' : ''}`}
                                    onClick={() => setFeatureFilter(f)}
                                >
                                    {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                                    {f !== 'all' && (
                                        <span className="feat-filter-count">
                                            {features.filter(x => x.status === f).length}
                                        </span>
                                    )}
                                    {f === 'all' && (
                                        <span className="feat-filter-count">{features.length}</span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {loadingFeatures ? (
                            <div className="clients-loading"><p>Loading feature requests…</p></div>
                        ) : features.filter(f => featureFilter === 'all' || f.status === featureFilter).length === 0 ? (
                            <div className="clients-empty">
                                <FaLightbulb />
                                <h3>No feature requests</h3>
                                <p>Client feature requests will appear here.</p>
                            </div>
                        ) : (
                            <div className="feat-cards">
                                {features
                                    .filter(f => featureFilter === 'all' || f.status === featureFilter)
                                    .map(f => (
                                        <div key={f.id} className={`feat-card feat-card--${f.status}`}>
                                            <div className="feat-card-header">
                                                <div className="feat-card-meta">
                                                    <span className="feat-client-name">{f.first_name} {f.last_name}</span>
                                                    <span className="feat-project-name">{f.project_name}</span>
                                                </div>
                                                <FeatStatusBadge status={f.status} />
                                            </div>
                                            <div className="feat-card-title">{f.title}</div>
                                            {f.description && (
                                                <div className="feat-card-desc">{f.description}</div>
                                            )}
                                            <div className="feat-card-footer">
                                                <span className="feat-date">{formatDate(f.created_at)}</span>
                                                <div className="feat-actions">
                                                    {f.status !== 'in_progress' && (
                                                        <button
                                                            className="feat-action-btn inprogress"
                                                            title="Mark In Progress"
                                                            disabled={!!featureAction}
                                                            onClick={() => handleFeatureStatus(f.id, 'in_progress')}
                                                        >
                                                            {featureAction === `in_progress-${f.id}` ? <FaSpinner className="spin" /> : <FaHourglassHalf />}
                                                            <span>In Progress</span>
                                                        </button>
                                                    )}
                                                    {f.status !== 'completed' && (
                                                        <button
                                                            className="feat-action-btn complete"
                                                            title="Mark Completed"
                                                            disabled={!!featureAction}
                                                            onClick={() => handleFeatureStatus(f.id, 'completed')}
                                                        >
                                                            {featureAction === `completed-${f.id}` ? <FaSpinner className="spin" /> : <FaCheckCircle />}
                                                            <span>Complete</span>
                                                        </button>
                                                    )}
                                                    {f.status !== 'rejected' && (
                                                        <button
                                                            className="feat-action-btn reject"
                                                            title="Reject"
                                                            disabled={!!featureAction}
                                                            onClick={() => handleFeatureStatus(f.id, 'rejected')}
                                                        >
                                                            {featureAction === `rejected-${f.id}` ? <FaSpinner className="spin" /> : <FaTimesCircle />}
                                                            <span>Reject</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Screenshot lightbox */}
                <AnimatePresence>
                    {viewScreenshot && (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setViewScreenshot(null)}
                        >
                            <motion.div
                                className="pay-screenshot-modal"
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                onClick={e => e.stopPropagation()}
                            >
                                <button className="modal-close" onClick={() => setViewScreenshot(null)}><FaTimes /></button>
                                <img src={viewScreenshot} alt="Payment Screenshot" />
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
};

export default AdminPayments;
